import { useState, useEffect, createContext, useContext } from 'react';
import liffManager from '../lib/liff';
import apiClient from '../lib/api';
import { LiffProfile } from '../types/liff';
import type { FamilyUser } from '../lib/api';
import { AuthEvents, ErrorEvents, setUserProperties, enableFullTracking } from '../lib/analytics';

interface AuthState {
  isLoading: boolean;
  isAuthenticated: boolean;
  profile: LiffProfile | null;
  user: FamilyUser | null;
  error: string | null;
}

interface AuthContextValue extends AuthState {
  login: () => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const useAuthState = () => {
  const [state, setState] = useState<AuthState>({
    isLoading: true,
    isAuthenticated: false,
    profile: null,
    user: null,
    error: null,
  });

  const login = async () => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));
      
      await liffManager.init();
      
      if (!liffManager.isLoggedIn()) {
        await liffManager.login();
        return;
      }

      await authenticateUser();
    } catch (error) {
      console.error('Login failed:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Login failed',
      }));
    }
  };

  const logout = () => {
    try {
      liffManager.logout();
      apiClient.clearIdToken();
      setState({
        isLoading: false,
        isAuthenticated: false,
        profile: null,
        user: null,
        error: null,
      });

      // Analytics: ログアウトイベント
      AuthEvents.logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const authenticateUser = async () => {
    try {
      // 常に最新のプロフィールとID Tokenを取得
      const [profile, idToken] = await Promise.all([
        liffManager.getProfile(),
        liffManager.getIdToken(), // LIFF SDKから最新のトークンを取得
      ]);

      // 新しいトークンをAPIクライアントに設定（localStorageにも保存）
      apiClient.setIdToken(idToken);

      // バックエンドでトークン検証とユーザー情報取得
      const user = await apiClient.verifyLineAuth(idToken);

      setState({
        isLoading: false,
        isAuthenticated: true,
        profile,
        user,
        error: null,
      });

      // Analytics: ログイン成功イベント
      AuthEvents.loginSuccess('line');

      // Analytics: ユーザープロパティ設定
      setUserProperties(profile.userId, {
        liffContext: liffManager.isInClient() ? 'app' : 'browser',
      });

      // フルトラッキングを有効化（操作ログの同意はアクセス前に取得済み）
      enableFullTracking();
    } catch (error) {
      console.error('Authentication failed:', error);

      // Analytics: ログイン失敗イベント
      const errorMessage = error instanceof Error ? error.message : 'Authentication failed';
      AuthEvents.loginFailed(errorMessage);
      ErrorEvents.liffError('authentication_failed', errorMessage);

      setState(prev => ({
        ...prev,
        isLoading: false,
        isAuthenticated: false,
        error: errorMessage,
      }));
    }
  };

  const refreshUser = async () => {
    if (!state.isAuthenticated) return;

    try {
      setState(prev => ({ ...prev, isLoading: true }));
      const idToken = await liffManager.getIdToken();
      const user = await apiClient.verifyLineAuth(idToken);
      
      setState(prev => ({
        ...prev,
        isLoading: false,
        user,
      }));
    } catch (error) {
      console.error('Failed to refresh user:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to refresh user data',
      }));
    }
  };

  // 修正: エラー処理を強化し、初期化をより慎重に行う
  const checkAuthState = async () => {
    try {
      // LIFF初期化をtry-catchで包む
      await liffManager.init();
      
      if (liffManager.isLoggedIn()) {
        await authenticateUser();
      } else {
        setState(prev => ({ ...prev, isLoading: false }));
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      // エラーが発生しても開発モードでは続行
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Authentication check failed',
      }));
    }
  };

  // 修正: useEffectをより安全にする
  useEffect(() => {
    // 非同期処理を安全に実行
    let isMounted = true;
    
    const initAuth = async () => {
      if (isMounted) {
        await checkAuthState();
      }
    };
    
    initAuth().catch(error => {
      console.error('Failed to initialize auth:', error);
      if (isMounted) {
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: 'Failed to initialize authentication',
        }));
      }
    });

    // クリーンアップ関数
    return () => {
      isMounted = false;
    };
  }, []);

  return {
    ...state,
    login,
    logout,
    refreshUser,
  };
};

export { AuthContext };
export default useAuth;